import { useCallback, useEffect, useMemo, useRef, useState } from "react";
function buildIndex(trees) {
    const metaById = new Map();
    const allExpandableIds = [];
    const walk = (list, parentId, depth) => {
        list.forEach((node) => {
            const hasChildren = Boolean(node.children && node.children.length > 0);
            if (process.env.NODE_ENV !== "production" && metaById.has(node.id)) {
                // eslint-disable-next-line no-console
                console.warn(`TreeView: duplicate node id "${node.id}". Node ids must be unique across all trees.`);
            }
            metaById.set(node.id, { parentId, depth, hasChildren });
            if (hasChildren) {
                allExpandableIds.push(node.id);
                walk(node.children, node.id, depth + 1);
            }
        });
    };
    trees.forEach((tree) => walk(tree.nodes, null, 0));
    return { metaById, allExpandableIds };
}
function ancestorsOf(id, metaById) {
    var _a, _b;
    var _c, _d;
    const chain = [];
    let current = (_c = (_a = metaById.get(id)) === null || _a === void 0 ? void 0 : _a.parentId) !== null && _c !== void 0 ? _c : null;
    while (current) {
        chain.push(current);
        current = (_d = (_b = metaById.get(current)) === null || _b === void 0 ? void 0 : _b.parentId) !== null && _d !== void 0 ? _d : null;
    }
    return chain;
}
/** Owns the tree's structure index, expansion state and the flattened list of visible rows. Expansion is uncontrolled by default (auto-opening the ancestors of `selected`), or controlled via `expanded`/`onExpandedChange`. */
export function useTreeModel({ trees, selected, defaultExpanded, expanded, onExpandedChange, }) {
    const { metaById, allExpandableIds } = useMemo(() => buildIndex(trees), [trees]);
    const isControlled = expanded !== undefined;
    const [internalExpanded, setInternalExpanded] = useState(() => {
        if (defaultExpanded === "all")
            return new Set(allExpandableIds);
        const set = new Set(defaultExpanded !== null && defaultExpanded !== void 0 ? defaultExpanded : []);
        if (selected)
            ancestorsOf(selected, metaById).forEach((id) => set.add(id));
        return set;
    });
    const expandedSet = useMemo(() => (isControlled ? new Set(expanded) : internalExpanded), [isControlled, expanded, internalExpanded]);
    const expandedSetRef = useRef(expandedSet);
    expandedSetRef.current = expandedSet;
    const commit = useCallback((next) => {
        if (!isControlled)
            setInternalExpanded(next);
        onExpandedChange === null || onExpandedChange === void 0 ? void 0 : onExpandedChange(Array.from(next));
    }, [isControlled, onExpandedChange]);
    const toggleExpanded = useCallback((id) => {
        const next = new Set(expandedSetRef.current);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        commit(next);
    }, [commit]);
    const expandNode = useCallback((id) => {
        if (expandedSetRef.current.has(id))
            return;
        const next = new Set(expandedSetRef.current);
        next.add(id);
        commit(next);
    }, [commit]);
    useEffect(() => {
        if (!selected)
            return;
        const meta = metaById.get(selected);
        if (!meta)
            return;
        const toOpen = ancestorsOf(selected, metaById);
        if (meta.hasChildren)
            toOpen.push(selected);
        const current = expandedSetRef.current;
        const missing = toOpen.filter((id) => !current.has(id));
        if (missing.length === 0)
            return;
        const next = new Set(current);
        missing.forEach((id) => next.add(id));
        commit(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, metaById]);
    const seenExpandableRef = useRef(new Set(allExpandableIds));
    useEffect(() => {
        if (defaultExpanded !== "all")
            return;
        const fresh = allExpandableIds.filter((id) => !seenExpandableRef.current.has(id));
        if (fresh.length === 0)
            return;
        fresh.forEach((id) => seenExpandableRef.current.add(id));
        const next = new Set(expandedSetRef.current);
        fresh.forEach((id) => next.add(id));
        commit(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultExpanded, allExpandableIds]);
    const { visibleRows, rowIndexById, separatedRowIndices } = useMemo(() => {
        const rows = [];
        const indexById = new Map();
        const separatedIndices = new Set();
        const walk = (list, depth, parentId, ancestors) => {
            list.forEach((node, position) => {
                const hasChildren = Boolean(node.children && node.children.length > 0);
                const isExpanded = hasChildren && expandedSet.has(node.id);
                const isLastChild = position === list.length - 1;
                indexById.set(node.id, rows.length);
                rows.push({
                    kind: "node",
                    id: node.id,
                    node,
                    depth,
                    hasChildren,
                    expanded: isExpanded,
                    setSize: list.length,
                    posInSet: position + 1,
                    parentId,
                    ancestors,
                });
                if (isExpanded) {
                    const childAncestors = parentId === null
                        ? ancestors
                        : [...ancestors, { groupId: parentId, continues: !isLastChild }];
                    walk(node.children, depth + 1, node.id, childAncestors);
                }
            });
        };
        trees.forEach((tree, treeIndex) => {
            const treeStartRow = rows.length;
            if (tree.title !== null && tree.title !== undefined) {
                rows.push({
                    kind: "title",
                    key: `tree-title-${treeIndex}`,
                    title: tree.title,
                    shortTitle: tree.shortTitle,
                });
            }
            walk(tree.nodes, 0, null, []);
            if (treeIndex > 0 && rows.length > treeStartRow) {
                separatedIndices.add(treeStartRow);
            }
        });
        return {
            visibleRows: rows,
            rowIndexById: indexById,
            separatedRowIndices: separatedIndices,
        };
    }, [trees, expandedSet]);
    return {
        visibleRows,
        rowIndexById,
        separatedRowIndices,
        toggleExpanded,
        expandNode,
    };
}
//# sourceMappingURL=useTreeModel.js.map